// Canonical effort-scale layer. The whole app pivots on ONE internal
// effort scale; the other is a pure read-time projection of it. This
// module is the single source of truth for that relationship so a stat
// aggregate never mixes a raw RPE from one set with a raw RIR from
// another (a garbage average — RPE 8 and RIR 2 are the SAME effort but
// opposite numbers).
//
// Definition (modern powerlifting, the one Antenore is learning):
//
//     RPE = 10 − RIR        RIR = 10 − RPE
//
// So RIR 2 ("two reps left in the tank") == RPE 8. Both run 0..10:
// RIR 0 / RPE 10 = total failure; RIR 10 / RPE 0 = trivially easy.
//
// Canonical internal unit = RIR (more concrete to a beginner: "how many
// reps did I have left?"). RPE is exposed only as a display projection,
// fixed to the RIR-based definition above so the conversion stays exact.

// The two scales JL understands. RIR is the canonical/default; RPE is the
// alternative display projection.
export const EFFORT_SCALES = ["rir", "rpe"] as const;
export type EffortScale = (typeof EFFORT_SCALES)[number];

// Both scales share the same 0..10 domain.
export const EFFORT_SCALE_MAX = 10;

// The RIR at/above which a set counts as "submaximal" — reps left in
// reserve, so it under-states true capacity. RIR 2 == RPE 8. This is the
// E1RM-TRUST threshold and nothing else: e1rmConfidence calls a set at or
// below it a "probable" ceiling, and the regression detector downgrades a
// decline built on RIR ≥ it to a plateau (an easy set's e1RM can't prove a
// lower ceiling).
//
// ⛔ It used to serve a SECOND job — counting working sets — and the two
// parted ways on 2026-09-04 (MSC-2). They are different questions that
// happened to share a number: "can I trust this e1RM as a ceiling?" is not
// "did this set train the muscle?". Splitting them, rather than moving the
// one constant, is what keeps a working-set decision from silently
// re-rating every e1RM in the app. Counting working sets → EFFORT_HARD_SET_RIR.
export const EFFORT_SUBMAXIMAL_RIR = 2;

// The RIR at/below which a set counts as a WORKING set — one that trained
// the muscle and belongs in a weekly hard-set count against an MEV/MAV/MRV
// band. RIR 3 == RPE 7: three reps in reserve is training volume, not a
// warm-up approach.
//
// Antenore set the line on 2026-09-04, measured on the program he actually
// follows. Tetras Advanced prescribes effort on all 176 of its sets, and at
// the old boundary (RPE ≥ 8) only 39 counted — the 73 sets at RPE 7 dropped
// out alongside the warm-ups. Asked whether RPE 7 is a working set: "si, i
// programmi attuali non hanno riscaldamento". At RIR ≤ 3 the count is 112,
// and his RPE 5 deload day stays out with RPE 6, in the effort meter's
// bottom `4+` step where the warm-up rule of §18.1a already put it.
//
// Everything above this line is an approach set: derived from the effort the
// athlete already gives per set, never a chip they tap and never a fraction
// of the day's top load (a real ramp puts 120x3 at 86% of a 140x6 top, so
// load is the wrong discriminant — measurement-contract.md §18.1a).
// Reuse this everywhere — never re-pick a threshold inline.
export const EFFORT_HARD_SET_RIR = 3;

// The RIR at/above which a set is an APPROACH — a warm-up rung the TIMELINE
// says out loud (MSC-2 slice 2). Antenore picked 6 on 2026-09-05 from three
// options that were put to him, the recommended one being this — a choice
// among proposals, not a proposal of his. next/measurement-contract.md
// §18.1a-ter carries who proposed what.
//
// ⛔ A THIRD threshold, never the inverse of EFFORT_HARD_SET_RIR. "Does not
// count as a working set" is not "is a warm-up", so RIR 4 and 5 fall between
// the two lines and must show NOTHING: they are real light work, and naming
// them on screen would be a lie. Full story + the measured band:
// next/measurement-contract.md §18.1a-ter.
export const EFFORT_APPROACH_RIR = 6;

// A set's EFFECTIVE RIR — the ONE canonical effort resolution, folding the
// two rules that used to live apart (MX-4):
//   1. fail ⇒ RIR 0. A failed set was taken to (or past) failure, so it
//      counts as maximal effort — the established convention (maxRegression's
//      ceiling-attempt gate + the AP-0p fail model: "fail is exactly the
//      maximal-effort signal").
//   2. rir ?? rpeToRir(rpe). RIR is the canonical unit, but legacy rows may
//      carry ONLY rpe (pre-single-column sets, and Leonardo logs in the RPE
//      scale). When rir is absent, derive it from rpe instead of dropping the
//      effort — never require both fields. rpe is a projection of rir
//      (effortScale.ts), so this recovers the real datum, it doesn't invent one.
// Returns null only when effort is genuinely unknown (no fail, no rir, no rpe)
// so a caller can tell an easy set from an untagged one. This is the single
// source for both rules; every effort reader (hard-set accumulator ≤ 2,
// ceiling attempt === 0, topSetRir, drift) derives from it, never re-tests
// `fail` or re-derives the rpe fallback inline.
export const effectiveRir = (set: {
  fail?: boolean;
  rir?: number | null;
  rpe?: number | null;
}): number | null => {
  if (set.fail === true) return 0;
  if (set.rir != null) return set.rir;
  if (set.rpe != null) return rpeToRir(set.rpe);
  return null;
};

// Is this set an approach? Resolves effort through effectiveRir like every
// other effort reader, so a failed set answers false (fail ⇒ RIR 0) and an
// rpe-only legacy row answers on the same footing as an rir one.
//
// ⛔ Returns false for an UNTAGGED set, and that is the point: no effort
// recorded is not "easy", it is unknown, and the feed must not invent a
// warm-up out of a blank ([[feedback_empty_vs_unresolved_collection]]).
export const isApproachSet = (set: {
  fail?: boolean;
  rir?: number | null;
  rpe?: number | null;
}): boolean => {
  const rir = effectiveRir(set);
  return rir !== null && rir >= EFFORT_APPROACH_RIR;
};

// Can this set be a session's TOP set / e1RM anchor? A set with a known easy
// effort (RIR > EFFORT_HARD_SET_RIR) cannot (#521); an effort-unknown set stays
// eligible so untagged history keeps its e1RM and PRs. The one predicate behind
// sessionResult's top set and the logger's anchor (#531).
export const isTopSetEligible = (set: {
  fail?: boolean;
  rir?: number | null;
  rpe?: number | null;
}): boolean => {
  const rir = effectiveRir(set);
  return rir == null || rir <= EFFORT_HARD_SET_RIR;
};

const clampEffort = (v: number): number =>
  Math.min(EFFORT_SCALE_MAX, Math.max(0, v));

// RPE → RIR and back. Clamped so an out-of-range input (a typo, a stale
// value) can never produce a negative or >10 effort downstream.
export const rpeToRir = (rpe: number): number => clampEffort(EFFORT_SCALE_MAX - rpe);
export const rirToRpe = (rir: number): number => clampEffort(EFFORT_SCALE_MAX - rir);

// Convert a value between scales. Identity when from === to. Used by the
// Logger to render a prescribed target (authored in either scale) in the
// athlete's preferred scale, and by the save path to derive the mirror
// field so a set always carries both rpe and rir in lockstep.
export const convertEffort = (
  value: number,
  from: EffortScale,
  to: EffortScale,
): number => {
  if (from === to) return value;
  return from === "rpe" ? rpeToRir(value) : rirToRpe(value);
};

// THE one place that spells an effort value out. Both the plan preview's
// prescribed target (previewFormat.ts) and the logger's recorded history row
// (LG-49) come through here, so "RIR 2" can never drift into "2 RIR" or
// "RIR2" on one surface only. Rounds to 2 decimals — half-step efforts
// ("8,5") are real, integers stay integers.
export const effortLabel = (value: number, scale: EffortScale): string =>
  `${scale === "rpe" ? "RPE" : "RIR"} ${Math.round(value * 100) / 100}`;

// What the athlete ACTUALLY did on a set, in the scale they read in — the
// read-side twin of the prescribed target label above (LG-49). Resolves the
// canonical RIR first (so a legacy rpe-only row and a failed set both answer)
// and projects it once, rather than printing whichever raw field happens to
// be populated: a row showing "RPE 8" beside another showing "RIR 2" would be
// stating the SAME effort in opposite numbers.
//
// ⛔ Returns null when the effort is genuinely unknown. An untagged set and an
// easy one are different facts, and a placeholder ("RIR —") would flatten them
// ([[feedback_empty_vs_unresolved_collection]]).
export const recordedEffortLabel = (
  set: { fail?: boolean; rir?: number | null; rpe?: number | null },
  scale: EffortScale,
): string | null => {
  const rir = effectiveRir(set);
  if (rir === null) return null;
  return effortLabel(convertEffort(rir, "rir", scale), scale);
};
